import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, first, Observable, switchMap, tap, throwError } from 'rxjs';

import { asserts } from '../../../core/utils/assertion';
import { AUTH_TOKEN } from '../auth-token.interceptor';
import { AuthService } from '../auth.service';
import { AbstractRefreshTokenAdaptor } from './abstract-refresh-token.adaptor';
import { RefreshTokenError, RefreshTokenErrorMessage } from './refresh-token-error';

export class AuthRefreshTokenInterceptor implements HttpInterceptor {
  private auth = inject(AuthService);
  private refreshTokenAdaptor = inject(AbstractRefreshTokenAdaptor, { optional: true });

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.context.get(AUTH_TOKEN) != null && this.auth.isTokenTypeRefreshToken()) {
      asserts(this.refreshTokenAdaptor, 'AbstractRefreshTokenAdaptor not provided');

      const tokenPayload = { ...this.auth.tokenInstance };

      if (this.auth.tokenInstance.isRefreshTokenExpire()) {
        const error = new RefreshTokenError(RefreshTokenErrorMessage.EXPIRED);

        this.refreshTokenAdaptor.onRefreshTokenFail(tokenPayload, error);

        return throwError(() => error);
      } else if (this.auth.tokenInstance.isAccessTokenExpire()) {
        return this.refreshTokenAdaptor.onRefreshToken({ ...this.auth.tokenInstance }).pipe(
          catchError((err: unknown) => {
            const error = new RefreshTokenError(RefreshTokenErrorMessage.FAILED, err);
            this.refreshTokenAdaptor!.onRefreshTokenFail(tokenPayload, error);

            return throwError(() => error);
          }),
          first(),
          tap((payload) => {
            this.auth.setToken(payload);
          }),
          switchMap((payload) => {
            const context = req.context.set(AUTH_TOKEN, payload.accessToken);
            return next.handle(req.clone({ context }));
          }),
        );
      }
    }
    return next.handle(req);
  }
}
